import { useMemo } from "react";
import { useAppAuth, useOptionalAppAuth } from "~/lib/auth/app-auth";
import { createPrincipalWalletSet } from "~/lib/auth/principal-wallets";
import { useVmPreference } from "~/lib/auth/vm-preference";
import { createSessionQueryScope } from "~/lib/query-keys";

type SessionScopeAuthInput = Pick<
  NonNullable<ReturnType<typeof useOptionalAppAuth>>,
  "authenticated" | "user"
>;

function useSessionScopeForAuth(auth: SessionScopeAuthInput) {
  const { preference } = useVmPreference();

  const primaryWalletAddress = useMemo(() => {
    const principalWallets = createPrincipalWalletSet({
      user: auth.user,
      vmPreference: preference,
    });
    return principalWallets.primaryWallet?.address ?? null;
  }, [auth.user, preference]);

  return useMemo(
    () =>
      createSessionQueryScope({
        authenticated: auth.authenticated,
        user: auth.user,
        vmPreference: preference,
        walletAddress: primaryWalletAddress,
      }),
    [auth.authenticated, auth.user, preference, primaryWalletAddress],
  );
}

export function useAppSessionScope() {
  return useSessionScopeForAuth(useAppAuth());
}

export function useOptionalAppSessionScope() {
  const auth = useOptionalAppAuth();

  return useSessionScopeForAuth(
    auth ?? {
      authenticated: false,
      user: null,
    },
  );
}
